import HelpCollapsible from './HelpCollapsible.jsx';
import AttributionsContent from './AttributionsContent.jsx';
import { WarnIcon, BottleIcon, VineIcon, HouseIcon, HeliIcon, MountainIcon } from './icons.jsx';
import { PRODUCER_TYPES } from '../data/producerTypes.js';

// The main info popup's content (everything scrollable between the
// title bar and the sticky footer), extracted VERBATIM from Sidebar so
// an atlas pack can shadow it (src/atlas/README.md). The German app
// renders byte-identically to the pre-extraction markup.

export const HELP_TITLE = 'Willkommen';

export default function HelpContent({ onNavigateExample, onClose }) {
  return (
    <>
          <div className="help-modal__section">
            <h3>How to use</h3>
            <p>Click any region in the sidebar to zoom in and see its vineyards. Click a bereich to zoom further. Click any vineyard polygon on the map to view its details. Sign in to edit vineyard data.</p>
            <p>The Layers button also offers a Key Producers view: Mike's curated producer list on one map, added to while studying, with a search that understands producer names and grape varieties.</p>
          </div>

          <div className="help-modal__section help-modal__section--example">
            <h3>Example</h3>
            <p className="help-modal__example-text">Start with Uhlen Blaufüsser Lay, a terraced Terrassenmosel wall above Winningen. Click the screenshot to jump straight to it, then work your way down the Mosel from there.</p>
            <button className="help-modal__example-shot" onClick={() => { onNavigateExample(); onClose(); }}>
              <img src="/uhlen-preview.jpg" alt="Uhlen Blaufüsser Lay above the Mosel, zoomed in with the vineyard sidebar open" />
              <span className="help-modal__example-shot-label">Explore the Mosel →</span>
            </button>
          </div>

          <HelpCollapsible title="Legend" defaultOpen>
            <p>Each region (Anbaugebiet) has a distinct color. When zoomed into a region, vineyards are colored by their bereich.</p>
            <h4 className="help-legend__subtitle">Map</h4>
            <dl className="help-legend">
              <div className="help-legend__row">
                <dt><span className="help-swatch help-swatch--verified"></span></dt>
                <dd>Verified vineyard. Solid border, fuller fill. Researched and confirmed with sources.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="help-swatch help-swatch--unverified"></span></dt>
                <dd>Unverified vineyard. Dashed border, faded fill. Raw OpenStreetMap data, may be incomplete.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="help-swatch help-swatch--notable"></span></dt>
                <dd>Notable vineyard. Gold fill. Highlighted in GWS course material.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="help-swatch help-swatch--vdpgg"></span></dt>
                <dd>Black fill marks a vineyard eligible for VDP Grosse Lage.</dd>
              </div>
              <div className="help-legend__row">
                <dt><HouseIcon size={13} style={{ color: '#d97706' }} /></dt>
                <dd>Notable wine village. Amber house on the map, toggled via the eye in the Key Villages section.</dd>
              </div>
              <div className="help-legend__row">
                <dt><BottleIcon size={17} /></dt>
                <dd>Producer with tasting notes. Green bottle, toggled via the eye in the Producers section. Added as I taste wines from each region.</dd>
              </div>
              <div className="help-legend__row">
                <dt><HeliIcon size={16} /></dt>
                <dd>Tour helicopter. Appears over a region's airfield when zoomed in. Click it to launch an interactive 3D valley tour (desktop only).</dd>
              </div>
              <div className="help-legend__row">
                <dt><MountainIcon size={15} style={{ color: '#6b7a63' }} /></dt>
                <dd>Mountain range. Marks the ranges in the Key Rivers &amp; Mtns layer; click one for its influence on the wine regions.</dd>
              </div>
            </dl>
            <h4 className="help-legend__subtitle">Sidebar</h4>
            <dl className="help-legend">
              <div className="help-legend__row">
                <dt><span className="detail__notable-star" style={{ marginLeft: 0, fontSize: '1rem' }}><VineIcon /></span></dt>
                <dd>Gold vine next to vineyard names marked as notable in GWS course material.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="detail__gg-badge" style={{ marginLeft: 0 }}>GG</span></dt>
                <dd>Großes Gewächs eligible. Vineyard qualifies for Germany's top dry wine classification.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="detail__gg-badge" style={{ marginLeft: 0 }}>1G</span></dt>
                <dd>VDP.ERSTE LAGE. Dry wines from this vineyard are 1G (Erste Lage trocken).</dd>
              </div>
              {/* producer types: only the kinds present in the data ride the
                  legend — the panel tooltip covers the rarer ones */}
              {['estate', 'cooperative'].map((k) => {
                const t = PRODUCER_TYPES[k];
                return (
                  <div className="help-legend__row" key={k}>
                    <dt><img src={t.icon} alt="" width="17" height="17" /></dt>
                    <dd>{t.label} ({t.german}). Rides next to a producer's name: {t.description}.</dd>
                  </div>
                );
              })}
              <div className="help-legend__row">
                <dt><span className="vdp-badge" style={{ marginLeft: 0 }}>VDP</span></dt>
                <dd>VDP member. Producer belongs to the Verband Deutscher Prädikatsweingüter.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="detail__harvest-badge" style={{ marginLeft: 0 }}>24</span></dt>
                <dd>Harvest worked. A producer where Mike worked the 2024 harvest; ignore this tag for studying.</dd>
              </div>
            </dl>
            <h4 className="help-legend__subtitle">Layers</h4>
            <p>Base views, switchable anytime from the Layers button on the map.</p>
            <dl className="help-legend help-legend--layers">
              <div className="help-legend__row">
                <dt><span className="help-thumb" style={{ backgroundImage: 'url(/map-thumb.png)' }} /></dt>
                <dd>Map. The default light base map, best for reading names and boundaries.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="help-thumb" style={{ backgroundImage: 'url(/sat-thumb.jpg)' }} /></dt>
                <dd>Satellite. Aerial imagery for studying terrain and slopes. Vineyard fills fade so the land shows through.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="help-thumb" style={{ backgroundImage: 'url(/climate/temp-thumb.png)' }} /></dt>
                <dd>Temp. Average yearly temperature over the map, from DWD 1991 to 2020 climate normals.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="help-thumb" style={{ backgroundImage: 'url(/climate/sun-thumb.png)' }} /></dt>
                <dd>Sun. Average yearly sunshine hours, from the same DWD dataset.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="help-thumb" style={{ backgroundImage: 'url(/climate/precip-thumb.png)' }} /></dt>
                <dd>Rain. Average monthly precipitation in region view, animated January to December with a month slider and play/pause.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="help-thumb" style={{ backgroundImage: 'url(/climate/terrain-thumb.png)' }} /></dt>
                <dd>Terrain. Shaded relief that shows the hillsides and how steep each site is. From Esri World Hillshade.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="help-thumb" style={{ backgroundImage: 'url(/producers-thumb.png)' }} /></dt>
                <dd>Key Producers. Producer map with per-region lists, a producer-or-grape search, and producer names on the map as you zoom in.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="help-thumb" style={{ backgroundImage: 'url(/geo-thumb.png)' }} /></dt>
                <dd>Key Rivers &amp; Mtns. The rivers and mountain ranges that shape German viticulture, each clickable for its influence on the wine regions.</dd>
              </div>
              <div className="help-legend__row">
                <dt><span className="help-thumb" style={{ backgroundImage: 'url(/flurb-thumb.png)' }} /></dt>
                <dd>Flurbereinigung. Official land-consolidation procedures for all Rheinland-Pfalz regions, with the vineyards they overlap marked in red.</dd>
              </div>
            </dl>
          </HelpCollapsible>

          <HelpCollapsible title="About this project">
            <p>This tool is being built and updated as part of my German Wine Scholar studies (and hopefully soon MW 🤞). The purpose is to manually research, verify, and fill in vineyard data as a way to learn the German wine landscape in depth.</p>
            <p>There may be errors or incomplete data. If you spot something wrong or would like access to collaborate, please reach out via <a href="https://www.linkedin.com/in/mike-rodden/" target="_blank" rel="noopener noreferrer">LinkedIn</a>.</p>
          </HelpCollapsible>

          <HelpCollapsible title="Common errors">
            <p><span style={{ color: '#9f1239' }}><WarnIcon size={12} style={{ verticalAlign: '-1.5px', marginRight: '0.3em' }} />Failed to fetch</span> — this app is 100% self-funded, so occasionally web hosting limits prevent data from loading. A simple browser refresh will usually solve the problem.</p>
          </HelpCollapsible>

          <HelpCollapsible title="Data, Sources & Attributions">
            <AttributionsContent />
          </HelpCollapsible>

          <HelpCollapsible title="Privacy">
            <p>This site uses Vercel's basic, cookieless analytics to count anonymous page visits, and that is the extent of it. Nothing identifies you personally: no cookies, no cross-site tracking, and no personal data is collected, stored, or used in any way.</p>
          </HelpCollapsible>
    </>
  );
}
